<template>
  <v-sheet class="mx-auto" max-width="2000">
    <v-card class="mx-auto" color="primary" variant="text" elevation="0"
            title="Exposure to certain drug types"
            ref="exposure-to-different-drug-types">
      <v-card-subtitle class="text-wrap">{{ drugTypeExposureSubtitle }}</v-card-subtitle>
      <v-row no-gutters>
        <v-col>
          <v-data-table :headers="drugTypeExposureHeaders"
                        :sort-by="drugTypeExposureSortBy"
                        :items="drugTypeExposureItems"
                        density="compact"
                        :fixed-header="true"
                        height="200px"
                        :items-per-page="0">
            <template #bottom></template>
            <template v-slot:no-data>
              <v-progress-circular color="primary" indeterminate></v-progress-circular>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col class="text-right">
          <vue-to-print :content="() => this.$refs['exposure-to-different-drug-types'].$el"
                        :document-title="icd + '-' + 'exposure-to-different-drug-types'"
                        remove-after-print>
            <template v-slot:trigger>
              <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
            </template>
          </vue-to-print>
        </v-col>
      </v-row>
    </v-card>
    <v-card class="mx-auto" color="primary" variant="text" elevation="0"
            title="Drug type used during follow-up time">
      <v-row align="center" no-gutters>
        <v-col>
          <v-card class="mx-auto" min-height="800" elevation="0" title=""
                  ref="drug-type-used-during-follow-up-time-chart">
            <v-row no-gutters>
              <v-col>
                <GChart type="ColumnChart"
                        @ready="drugTypeUsedDuringFollowUpTimeChartReady"
                        :data="drugTypeUsedDuringFollowUpTimeChartData"
                        :options="drugTypeUsedDuringFollowUpTimeChartOptions"
                        :key="'drugTypeUsedDuringFollowUpTimeChart' + drugTypeUsedDuringFollowUpTimeChartKeyIndex"
                        v-if="drugTypeUsedDuringFollowUpTimeChartData.length !== 0"/>
                <v-progress-circular color="primary" indeterminate v-else></v-progress-circular>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['drug-type-used-during-follow-up-time-chart'].$el"
                              :document-title="icd + '-' + 'drug-type-used-during-follow-up-time-chart'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
    <v-card class="mx-auto" color="primary" variant="text" elevation="0"
            title="Specific questions regarding drug exposure"
            ref="exposure-to-different-drug-types-specific-questions">
      <v-row no-gutters>
        <v-col>
          <v-data-table :headers="drugTypeExposureSpecificQuestionsHeaders"
                        :items="drugTypeExposureSpecificQuestionsItems"
                        density="compact"
                        :fixed-header="true"
                        :items-per-page="0">
            <template #bottom></template>
            <template v-slot:no-data>
              <v-progress-circular color="primary" indeterminate></v-progress-circular>
            </template>
            <template v-slot:[`item.question`]="{ item }">
              <v-row v-for="question in item.question" :key="question" no-gutters>
                {{ question }}
              </v-row>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col class="text-right">
          <vue-to-print :content="() => this.$refs['exposure-to-different-drug-types-specific-questions'].$el"
                        :document-title="icd + '-' + 'exposure-to-different-drug-types-specific-questions'"
                        remove-after-print>
            <template v-slot:trigger>
              <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
            </template>
          </vue-to-print>
        </v-col>
      </v-row>
    </v-card>
  </v-sheet>
</template>

<script>
import {defineComponent} from 'vue';
import {GChart} from 'vue-google-charts'
import axios from "axios";
import { VueToPrint } from "vue-to-print";

export default defineComponent({
  name: 'RareDiseaseTreatmentPatternPrescribedDrugs',
  props: {
    icd: String,
    visible: Boolean
  },
  components: {
    GChart,
    VueToPrint
  },
  data: () => ({
    drugTypeExposureSubtitle: '',
    drugTypeExposureHeaders: [
      {
        title: 'Drug type',
        align: 'start',
        key: 'drugType',
      },
      {
        title: 'Number of unique patients',
        align: 'start',
        key: 'numberOfUniquePatients',
      },
      {
        title: 'Patients at risk (N)',
        align: 'start',
        key: 'patientsAtRisk'
      }
    ],
    drugTypeExposureSortBy: [{ key: 'drugType' }],
    drugTypeExposureItems: [],
    drugTypeUsedDuringFollowUpTimeChartKeyIndex: 0,
    drugTypeUsedDuringFollowUpTimeChartSelectedSeries: undefined,
    drugTypeUsedDuringFollowUpTimeChartData: [],
    drugTypeUsedDuringFollowUpTimeChartOptions: {
      backgroundColor: {
        fill: '#364c5b'
      },
      title: 'Drug type used during follow-up time',
      titlePosition: 'out',
      titleTextStyle: {
        color: '#ffffff',
        bold: false
      },
      height: 800,
      colors: ['#fb9a99', '#b2df8a', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c', '#bdbdc7', '#a6cee3'],
      legend: {
        position: 'top',
        alignment: 'end',
        maxLines: 5,
        textStyle: {
          color: '#c0c0c0',
          fontSize: 12
        },
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      },
      isStacked: true,
      series: {
        0: {
          targetAxisIndex: 1,
          type: 'line',
          pointShape: 'diamond',
          pointSize: 10
        },
        1: {
          targetAxisIndex: 0
        }
      },
      hAxis: {
        title: 'Year',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        },
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        }
      },
      vAxes: {
        0: {
          title: 'Number of patients',
          titleTextStyle: {
            color: '#f0f0f0',
            italic: false
          },
          gridlines: {
            count: 0
          },
          minorGridlines: {
            count: 0
          },
          textStyle: {
            color: '#ffffff',
            fontSize: 12
          }
        },
        1: {
          title: 'Patients at risk (N)',
          titleTextStyle: {
            color: '#f0f0f0',
            italic: false
          },
          gridlines: {
            color: '#364c5b',
            count: 0
          },
          minorGridlines: {
            color: '#364c5b',
            count: 0
          },
          textStyle: {
            color: '#ffffff',
            fontSize: 12
          }
        }
      },
      intervals: {
        'style': 'boxes',
        'boxWidth': 1,
        'lineWidth': 0,
        'fillOpacity': 0.2
      },
      tooltip: {
        isHtml: true
      },
      annotations: {
        textStyle: {
          fontSize: 12,
          color: '#808080',
          auraColor: 'none'
        }
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    },
    drugTypeExposureSpecificQuestionsHeaders: [
      {
        title: 'Question',
        align: 'start',
        key: 'question',
        sortable: false
      },
      {
        title: 'Mean',
        align: 'start',
        key: 'mean',
      },
      {
        title: 'Standard deviation',
        align: 'start',
        key: 'std'
      }
    ],
    drugTypeExposureSpecificQuestionsItems: []
  }),
  watch: {
    drugTypeUsedDuringFollowUpTimeChartSelectedSeries() {
      this.loadDrugTypeUsedDuringFollowUpTimeChartData(
          this.drugTypeUsedDuringFollowUpTimeChartSelectedSeries).then((response) => {
        this.drugTypeUsedDuringFollowUpTimeChartData = response.data.data
        this.drugTypeUsedDuringFollowUpTimeChartOptions.title = response.data.comment
      })
    },
  },
  methods: {
    forceRerender() {
      this.drugTypeUsedDuringFollowUpTimeChartKeyIndex += 1;
    },
    async loadDrugTypeExposureData() {
      return axios.get('rare-disease/drug-type-exposures',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
    },
    async loadDrugTypeUsedDuringFollowUpTimeChartData(atcGroup) {
      return axios.get('rare-disease/drug-type-used-during-follow-up-time',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              atc_group: atcGroup
            }
          });
    },
    async drugTypeUsedDuringFollowUpTimeChartReady(chart, google) {
      const setSeriesChartSelection = (selectedColumn) => {
        let selectedSeries = this.drugTypeUsedDuringFollowUpTimeChartData[0][selectedColumn]
        if (this.drugTypeUsedDuringFollowUpTimeChartSelectedSeries === selectedSeries) {
          this.drugTypeUsedDuringFollowUpTimeChartSelectedSeries = undefined
        } else {
          this.drugTypeUsedDuringFollowUpTimeChartSelectedSeries = selectedSeries
        }
      }

      const resetSeriesChartSelection = () => {
        this.drugTypeUsedDuringFollowUpTimeChartSelectedSeries = undefined
      }

      const seriesSelectHandler = function () {
        const selection = chart.getSelection()
        if (selection.length === 0) {
          resetSeriesChartSelection()
          return
        }
        const selectedItem = selection[0];
        setSeriesChartSelection(selectedItem.column)
      }

      google.visualization.events.addListener(chart, 'select', seriesSelectHandler);
    },
    async loadDrugTypeExposureSpecificQuestionsData() {
      return axios.get('rare-disease/drug-type-exposures-specific-questions',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
    }
  },
  async mounted() {
    await Promise.all([
      this.loadDrugTypeExposureData(),
      this.loadDrugTypeUsedDuringFollowUpTimeChartData(),
      this.loadDrugTypeExposureSpecificQuestionsData()
    ]).then(responses => {
      this.drugTypeExposureItems = responses[0].data.data
      this.drugTypeExposureSubtitle = responses[0].data.comment
      this.drugTypeUsedDuringFollowUpTimeChartData = responses[1].data.data
      this.drugTypeUsedDuringFollowUpTimeChartOptions.title = responses[1].data.comment
      this.drugTypeExposureSpecificQuestionsItems = responses[2].data
    })
  },
  updated() {
    if (this.visible) {
      this.forceRerender()
    }
  }
});
</script>

<style scoped>
@media print {
  .mx-auto {
    zoom: 50%;
  }
}
</style>
